const THREE_SRC = window.__MC3D_THREE || 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';
let threeP;
const loadThree = () => (threeP = threeP || import(THREE_SRC));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const near = (from, to) => from + (((to - from + Math.PI) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) - Math.PI;
/* Apple design (WWDC "Designing Fluid Interfaces"):
   - project(): adonde va el gesto, con la misma deceleracion exponencial que el scroll.
   - rubber(): resistencia progresiva al pasar el limite, en vez de un tope duro.
   - spring(): muelle criticamente amortiguado que conserva la velocidad, asi el
     giro continua a la velocidad del dedo al soltar y se puede volver a coger. */
const DECEL = 0.985;
const project = (v, d = DECEL) => (v / 1000) * d / (1 - d);
/* Muelle de inercia sincronizado con project(): con w = 1000(1-d)/d y una
   velocidad inicial v = w * distancia, el giro decelera de forma exponencial
   (igual que el scroll) y se detiene justo en el destino, sin pasarse. */
const GLIDE_W = (1000 * (1 - DECEL)) / DECEL;
const rubber = (v, lo, hi, dim = 0.35, c = 0.55) => {
if (v > hi) { const o = v - hi; return hi + (o * dim * c) / (dim + c * o); }
if (v < lo) { const o = lo - v; return lo - (o * dim * c) / (dim + c * o); }
return v;
};
const SPRING_W = (2 * Math.PI) / 0.45;
const spring = (x, v, target, dt, w = SPRING_W) => {
for (let n = Math.max(1, Math.ceil(dt * 240)), h = dt / n; n > 0; n--) {
v += (w * w * (target - x) - 2 * w * v) * h; x += v * h;
}
return [x, v];
};
const PY = -2.35;
const COLORS = { glacier: [0x8fb4ca, 0xa3c2d4], burgundy: [0x5c1d2a, 0x6a2533], silver: [0xd4d6d9, 0xe4e5e7], black: [0x2b2c30, 0x35373c] };
const OFF = { phone: -3.4, shell: -1.5, shield: 1.3, card: 3.0, pocket: 4.6 };
class Viewer {
constructor(T, el, art) {
this.T = T; this.el = el; this.d = el.dataset; this.art = art;
this.stage = el.querySelector('.mc3d__stage');
const canvas = document.createElement('canvas');
canvas.className = 'mc3d__canvas';
this.stage.prepend(canvas);
const r = (this.renderer = new T.WebGLRenderer({ canvas, antialias: true, alpha: true }));
r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
r.toneMapping = T.ACESFilmicToneMapping;
r.toneMappingExposure = 1.05;
this.scene = new T.Scene();
this.camera = new T.PerspectiveCamera(28, 1, 5, 160);
this.scene.environment = this.env();
this.scene.add(new T.AmbientLight(0xffffff, 0.35));
const key = new T.DirectionalLight(0xffffff, 1.7); key.position.set(6, 9, 12); this.scene.add(key);
const rim = new T.DirectionalLight(0xbfe3ff, 0.9); rim.position.set(-9, -3, -8); this.scene.add(rim);
this.build();
this.labels();
this.s = { rotY: -2.6, rotX: 0.16, tY: -0.55, tX: 0.16, open: 0, tOpen: 0, mode: 'spin', drag: false, last: 0, touched: false, vY: 0, vX: 0, rawX: 0.16, glide: false };
this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
this.ui();
this.resize();
new ResizeObserver(() => this.resize()).observe(this.stage);
this.clock = new T.Clock();
this.visible = true;
new IntersectionObserver((es) => { this.visible = es[0].isIntersecting; if (this.visible) { this.clock.getDelta(); this.loop(); } }).observe(el);
if (window.__MC3D_MAXDT) window.__mc3dv = this;
el.classList.add('is-ready');
setTimeout(() => { if (!this.s.touched && this.s.mode === 'spin') this.mode('open'); }, 2600);
this.loop();
}
env() {
const T = this.T, pm = new T.PMREMGenerator(this.renderer), s = new T.Scene();
s.add(new T.Mesh(new T.BoxGeometry(30, 30, 30), new T.MeshBasicMaterial({ color: 0x2c2c2c, side: T.BackSide })));
const panel = (w, h, x, y, z, i) => {
const m = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color: new T.Color(1, 1, 1).multiplyScalar(i), side: T.DoubleSide }));
m.position.set(x, y, z); m.lookAt(0, 0, 0); s.add(m);
};
panel(16, 4, 0, 13, 4, 4); panel(4, 16, -13, 0, 5, 2.4); panel(4, 14, 13, 2, -3, 2); panel(12, 10, 2, -2, 14, 1.3);
const tex = pm.fromScene(s, 0.035).texture; pm.dispose(); return tex;
}
rr(w, h, r, cx = 0, cy = 0, hole = false) {
const s = hole ? new this.T.Path() : new this.T.Shape(), x = cx - w / 2, y = cy - h / 2;
s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
return s;
}
ext(shape, depth, bevel = 0.04) {
const g = new this.T.ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3, curveSegments: 18 });
g.translate(0, 0, -depth / 2); return g;
}
tex(canvas, w, h, cy = 0) {
const t = new this.T.CanvasTexture(canvas);
t.colorSpace = this.T.SRGBColorSpace;
t.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
t.repeat.set(1 / w, 1 / h); t.offset.set(0.5, 0.5 - cy / h);
return t;
}
build() {
const T = this.T;
this.root = new T.Group(); this.scene.add(this.root);
this.parts = {};
const part = (n) => { const g = new T.Group(); this.root.add(g); this.parts[n] = g; return g; };
const edgeM = new T.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.42, depthWrite: false });
const clear = (o) => new T.MeshPhysicalMaterial({ color: 0xeaf7f6, roughness: 0.05, metalness: 0, transparent: true, opacity: o, clearcoat: 1, clearcoatRoughness: 0.04, side: T.DoubleSide, depthWrite: false, envMapIntensity: 1.5 });
const clearM = clear(0.2), clearThin = clear(0.1);
const acrylic = (shape, depth, mat, z) => {
const g = this.ext(shape, depth, 0), m = new T.Mesh(g, mat);
m.add(new T.LineSegments(new T.EdgesGeometry(g, 30), edgeM)); m.position.z = z; return m;
};
const [cMetal, cGlass] = COLORS[this.d.color] || COLORS.silver;
const dark = this.d.color === 'black' || this.d.color === 'burgundy';
const metal = new T.MeshPhysicalMaterial({ color: cMetal, metalness: dark ? 0.8 : 0.55, roughness: 0.3, clearcoat: 0.3, clearcoatRoughness: 0.3 });
const phone = part('phone');
phone.add(new T.Mesh(this.ext(this.rr(6.93, 14.74, 1.05), 0.615, 0.13), metal));
const glass = new T.Mesh(new T.ShapeGeometry(this.rr(6.86, 10.7, 1.0, 0, -2.06), 18), new T.MeshPhysicalMaterial({ color: cGlass, metalness: 0.15, roughness: 0.62, clearcoat: 0.35, clearcoatRoughness: 0.5 }));
glass.position.z = 0.446; phone.add(glass);
const screen = new T.Mesh(new T.ShapeGeometry(this.rr(6.75, 14.55, 0.98), 18), new T.MeshPhysicalMaterial({ color: 0x050608, roughness: 0.06, clearcoat: 1 }));
screen.position.z = -0.446; screen.rotation.y = Math.PI; phone.add(screen);
const plat = new T.Group(); plat.position.set(0, 5.48, 0.4375); phone.add(plat);
const pl = new T.Mesh(this.ext(this.rr(7.0, 3.95, 0.95), 0.12, 0.05), metal); pl.position.z = 0.105; plat.add(pl);
const top = 0.215;
const ringM = new T.MeshPhysicalMaterial({ color: 0x1c1d20, metalness: 0.9, roughness: 0.25, clearcoat: 1 });
const rimLM = new T.MeshStandardMaterial({ color: 0xe3e6eb, metalness: 1, roughness: 0.14 });
const lensM = new T.MeshPhysicalMaterial({ color: 0x05060a, roughness: 0.03, clearcoat: 1 });
const eyeM = new T.MeshPhysicalMaterial({ color: 0x14254d, metalness: 0.6, roughness: 0.1, clearcoat: 1 });
const cyl = (r, h, m, x, y, z, seg = 48) => { const c = new T.Mesh(new T.CylinderGeometry(r, r, h, seg), m); c.rotation.x = Math.PI / 2; c.position.set(x, y, z); plat.add(c); return c; };
[[-2.2, 0.76], [-2.2, -1.08], [-0.5, -0.18]].forEach(([x, y]) => {
cyl(0.8, 0.24, rimLM, x, y, top + 0.12);
cyl(0.68, 0.26, ringM, x, y, top + 0.14);
cyl(0.54, 0.02, lensM, x, y, top + 0.28);
cyl(0.25, 0.02, eyeM, x, y, top + 0.292, 32);
});
cyl(0.3, 0.04, new T.MeshStandardMaterial({ color: 0xfff1cc, roughness: 0.35, emissive: 0x332a10 }), 2.45, 0.8, top + 0.02, 32);
cyl(0.28, 0.04, lensM, 2.45, -1.08, top + 0.02, 32);
cyl(0.06, 0.03, lensM, 2.45, -0.15, top + 0.015, 16);
const shell = part('shell');
const rimS = this.rr(7.62, 15.42, 1.3); rimS.holes.push(this.rr(7.27, 15.07, 1.12, 0, 0, true));
shell.add(acrylic(rimS, 1.12, clearM, 0));
shell.add(acrylic(this.rr(7.5, 10.66, 1.2, 0, -2.17), 0.08, clearThin, 0.5));
const lipS = this.rr(7.5, 4.45, 1.05, 0, 5.45); lipS.holes.push(this.rr(7.2, 4.15, 0.98, 0, 5.45, true));
shell.add(acrylic(lipS, 0.5, clearM, 0.75));
const shield = part('shield');
const sh = new T.Mesh(this.ext(this.rr(6.5, 9.0, 0.36, 0, PY), 0.03, 0.015), new T.MeshStandardMaterial({ color: 0xc4cc52, metalness: 0.4, roughness: 0.3, transparent: true, opacity: 0.5, depthWrite: false }));
const shFace = new T.Mesh(new T.ShapeGeometry(this.rr(6.5, 9.0, 0.36, 0, PY), 12), new T.MeshBasicMaterial({ map: this.tex(this.art.shield(this.d.shieldTex), 6.5, 9.0, PY), transparent: true, depthWrite: false }));
shFace.position.z = 0.04; sh.add(shFace); sh.position.z = 0.6; shield.add(sh);
const card = part('card');
const cardBody = new T.Mesh(this.ext(this.rr(6.3, 8.8, 0.3, 0, PY), 0.02, 0.01), new T.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.6 }));
const cardFace = new T.Mesh(new T.ShapeGeometry(this.rr(6.3, 8.8, 0.3, 0, PY), 12), new T.MeshPhysicalMaterial({ map: this.tex(this.art.card(), 6.3, 8.8, PY), roughness: 0.35, clearcoat: 0.8, clearcoatRoughness: 0.2 }));
cardFace.position.z = 0.026; cardBody.add(cardFace); cardBody.position.z = 0.68; card.add(cardBody);
const pocket = part('pocket');
const frS = this.rr(6.95, 9.45, 0.55, 0, PY); frS.holes.push(this.rr(6.55, 9.05, 0.38, 0, PY, true));
const fr = acrylic(frS, 0.38, clearM, 0.74); pocket.add(fr);
const cover = acrylic(this.rr(6.87, 9.37, 0.52, 0, PY), 0.06, clearThin, 0.965); pocket.add(cover);
cover.renderOrder = 3; fr.renderOrder = 2;
const N = 80, pos = new Float32Array(N * 3), col = new Float32Array(N * 3);
this.heat = { N, pos, col, t: new Float32Array(N).map(() => Math.random()), sp: new Float32Array(N).map(() => 0.35 + Math.random() * 0.45) };
for (let i = 0; i < N; i++) { pos[i * 3] = (Math.random() - 0.5) * 6; pos[i * 3 + 1] = PY + (Math.random() - 0.5) * 8.4; }
const pg = new T.BufferGeometry();
pg.setAttribute('position', new T.BufferAttribute(pos, 3)); pg.setAttribute('color', new T.BufferAttribute(col, 3));
const dot = document.createElement('canvas'); dot.width = dot.height = 64;
const dg = dot.getContext('2d'), gr = dg.createRadialGradient(32, 32, 0, 32, 32, 32);
gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.4, 'rgba(255,255,255,.5)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
dg.fillStyle = gr; dg.fillRect(0, 0, 64, 64);
this.points = new T.Points(pg, new T.PointsMaterial({ size: 0.8, map: new T.CanvasTexture(dot), vertexColors: true, transparent: true, depthWrite: false, blending: T.AdditiveBlending }));
this.root.add(this.points);
}
labels() {
const box = this.stage.querySelector('.mc3d__labels'), d = this.d;
const defs = [
{ k: 'heat', part: 'phone', p: [-3.6, 1.2, 0.45], tone: 'hot', show: 'open', side: 'l' },
{ k: 'shield', part: 'shield', p: [-3.2, PY - 2.4, 0.62], tone: 'accent', show: 'open', side: 'l' },
{ k: 'card', part: 'card', p: [3.1, PY + 3.4, 0.7], tone: 'good', show: 'open', side: 'r' },
{ k: 'case', part: 'pocket', p: [3.45, PY - 3.4, 0.95], tone: 'plain', show: 'open', side: 'r' },
{ k: 'edge', part: 'pocket', p: [3.48, PY + 3.0, 0.93], tone: 'accent', show: 'edge', side: 'r' },
];
const legend = this.el.querySelector('.mc3d__legend');
let n = 0;
this.lab = defs.map((def) => {
const el = document.createElement('div');
el.className = `mc3d__label mc3d__label--${def.tone} mc3d__label--${def.k}`;
const num = def.show === 'open' ? ++n : '';
const i = document.createElement('i'); i.textContent = num; el.appendChild(i);
const span = document.createElement('span'); span.textContent = d[def.k] || ''; el.appendChild(span);
box.appendChild(el);
if (legend && num) {
const li = document.createElement('li'); li.className = `mc3d__label--${def.tone}`;
const b = document.createElement('i'); b.textContent = num; li.appendChild(b);
li.appendChild(document.createTextNode(d[def.k] || '')); legend.appendChild(li);
}
return { ...def, el, v: new this.T.Vector3(), a: 0 };
});
}
ui() {
const c = this.renderer.domElement, s = this.s;
this.btns = [...this.el.querySelectorAll('[data-mc3d-mode]')];
this.btns.forEach((b) => b.addEventListener('click', () => { this.touch(); this.mode(b.dataset.mc3dMode); }));
let id = null, px = 0, py = 0, hist = [];
c.addEventListener('pointerdown', (e) => {
id = e.pointerId; px = e.clientX; py = e.clientY; s.drag = true; c.setPointerCapture(id); this.touch();
/* Se coge desde donde esta en pantalla, no desde el objetivo: sin saltos. */
s.tY = s.rotY; s.tX = s.rotX; s.rawX = s.rotX; s.vY = 0; s.vX = 0; s.glide = false;
hist = [{ t: e.timeStamp, x: e.clientX, y: e.clientY }];
});
c.addEventListener('pointermove', (e) => {
if (!s.drag || e.pointerId !== id) return;
s.tY += (e.clientX - px) * 0.011;
s.rawX += (e.clientY - py) * 0.007; s.tX = rubber(s.rawX, -0.7, 0.7);
px = e.clientX; py = e.clientY; s.last = performance.now();
hist.push({ t: e.timeStamp, x: e.clientX, y: e.clientY });
while (hist.length > 2 && e.timeStamp - hist[0].t > 100) hist.shift();
this.loop();
});
const up = (e) => {
if (!s.drag) return;
s.drag = false; id = null; s.last = performance.now();
/* Velocidad en el instante de soltar: solo los ultimos 50 ms, contando el
   propio punto de soltar. Si el dedo se habia parado, la velocidad es 0 y el
   modelo se queda donde lo dejaste.
   pointercancel = el navegador se quedo el gesto para hacer scroll: sin inercia. */
let vx = 0;
if (e.type === 'pointerup' && !this.reduce) {
const now = e.timeStamp, pts = hist.filter((p) => now - p.t <= 50);
pts.push({ t: now, x: e.clientX, y: e.clientY });
const a = pts[0], b = pts[pts.length - 1], span = (b.t - a.t) / 1000;
if (span >= 0.012) vx = (b.x - a.x) / span;
}
if (Math.abs(vx) > 250) {
const dist = clamp(project(vx) * 0.011, -1.6, 1.6);
s.tY = s.rotY + dist; s.vY = dist * GLIDE_W; s.glide = true;
} else {
s.tY = s.rotY; s.vY = 0;
}
s.vX = 0;
s.rawX = clamp(s.tX, -0.7, 0.7); s.tX = s.rawX;
this.loop();
};
c.addEventListener('pointerup', up); c.addEventListener('pointercancel', up);
}
touch() { this.s.touched = true; this.s.last = performance.now(); this.el.classList.add('is-touched'); }
mode(m) {
const s = this.s;
if (m === 'open' && s.mode === 'open') m = 'spin';
s.mode = m; s.glide = false;
if (m === 'open') { s.tOpen = 1; s.tY = near(s.rotY, 0.98); s.tX = 0.22; }
else if (m === 'edge') { s.tOpen = 0; s.tY = near(s.rotY, 1.36); s.tX = 0.06; }
else { s.tOpen = 0; s.tX = 0.16; }
this.el.classList.toggle('is-open', m === 'open');
this.btns.forEach((b) => {
const on = b.dataset.mc3dMode === m;
b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on);
if (b.dataset.mc3dMode === 'open') b.textContent = on ? b.dataset.labelClose : b.dataset.labelOpen;
});
this.loop();
}
resize() {
const w = this.stage.clientWidth, h = this.stage.clientHeight;
if (!w || !h) return;
this.w = w; this.h = h;
this.renderer.setSize(w, h, false);
const a = w / h, t = Math.tan((this.camera.fov * Math.PI) / 360);
const dist = Math.max(18.5 / 2 / t, 13.5 / 2 / (t * a));
this.dist = dist; this.camera.aspect = a; this.camera.position.set(0, 0.6, dist); this.camera.lookAt(0, -0.2, 0);
this.camera.updateProjectionMatrix();
}
loop() {
if (this.raf) return;
this.raf = requestAnimationFrame(() => { this.raf = 0; this.frame(); if (this.visible) this.loop(); });
}
frame() {
const s = this.s, dt = Math.min(this.clock.getDelta(), window.__MC3D_MAXDT || 0.05), now = performance.now();
if (s.mode === 'spin' && !s.drag && !this.reduce && now - s.last > 2500) s.tY += dt * 0.4;
const ko = 1 - Math.pow(0.012, dt);
if (s.drag) {
/* Seguimiento 1:1: el modelo va pegado al dedo, sin retraso. */
s.rotY = s.tY; s.rotX = s.tX;
} else {
[s.rotY, s.vY] = spring(s.rotY, s.vY, s.tY, dt, s.glide ? GLIDE_W : SPRING_W);
if (s.glide && Math.abs(s.tY - s.rotY) < 0.002 && Math.abs(s.vY) < 0.02) s.glide = false;
[s.rotX, s.vX] = spring(s.rotX, s.vX, s.tX, dt);
}
s.open += (s.tOpen - s.open) * ko;
const o = s.open * s.open * (3 - 2 * s.open);
this.root.rotation.set(s.rotX, s.rotY, 0);
this.camera.position.z = this.dist * (1 + 0.5 * o);
this.root.position.x = -1.7 * o;
this.root.position.y = this.reduce ? 0 : Math.sin(now / 1400) * 0.12;
for (const n in OFF) this.parts[n].position.z = OFF[n] * o;
const H = this.heat, z0 = OFF.phone * o + 0.5, z1 = OFF.shield * o + 0.55, span = Math.max(z1 - z0, 0.01);
for (let i = 0; i < H.N; i++) {
H.t[i] += dt * H.sp[i] * (0.6 + 1.4 / span);
if (H.t[i] >= 1) { H.t[i] = 0; H.pos[i * 3] = (Math.random() - 0.5) * 6; H.pos[i * 3 + 1] = PY + (Math.random() - 0.5) * 8.4; }
const t = H.t[i], b = Math.min(1, Math.sin(Math.PI * t) * o * 1.3);
H.pos[i * 3 + 2] = z0 + span * t;
H.col[i * 3] = b; H.col[i * 3 + 1] = b * 0.42; H.col[i * 3 + 2] = b * 0.12;
}
this.points.geometry.attributes.position.needsUpdate = true;
this.points.geometry.attributes.color.needsUpdate = true;
const edgeW = s.mode === 'edge' ? 1 : 0;
this.scene.updateMatrixWorld();
for (const L of this.lab) {
const target = L.show === 'open' ? (o > 0.85 ? 1 : 0) : edgeW * (Math.abs(s.rotY - s.tY) < 0.25 ? 1 : 0);
L.a += (target - L.a) * (1 - Math.pow(0.0005, dt));
L.v.set(...L.p); this.parts[L.part].localToWorld(L.v); L.v.project(this.camera);
const x = (L.v.x + 1) / 2 * this.w, y = (1 - L.v.y) / 2 * this.h;
const lw = L.w || (L.w = L.el.offsetWidth || 150);
let left = L.side === 'l';
if (!left && x + lw > this.w - 6) left = true;
else if (left && x - lw < 6) left = false;
L.el.classList.toggle('is-left', left);
L.el.style.opacity = L.a.toFixed(3);
L.el.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) translate(${left ? '-100%' : '0'},-50%)`;
}
this.renderer.render(this.scene, this.camera);
}
}
function boot(el) {
Promise.all([loadThree(), import(el.dataset.art)]).then(([T, art]) => { try { new Viewer(T, el, art); } catch (e) { console.warn('[MC3D]', e); el.classList.add('is-failed'); } })
.catch(() => el.classList.add('is-failed'));
}
function init(root) {
(root || document).querySelectorAll('[data-mc3d]:not([data-mc3d-init])').forEach((el) => {
el.setAttribute('data-mc3d-init', '');
const io = new IntersectionObserver((es, obs) => { if (es.some((e) => e.isIntersecting)) { obs.disconnect(); boot(el); } }, { rootMargin: '300px 0px' });
io.observe(el);
});
}
init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
